import { useState, useEffect, useCallback } from 'react'
import { api } from '../utils/api'
import { formatRackName, rowIndexToLetter, rackLabel } from '../utils/rackName.logic'

export { formatRackName, rowIndexToLetter, rackLabel }

export interface DcMeta {
  code: string;
  name: string;
  rack_naming_template?: string;
  prefix?: string;
  trunk_rack_index?: number;
  rows_count?: number;
  max_racks_per_row?: number;
  cable_trays_json?: unknown;
  aisle_zones_json?: unknown;
}

export interface RackMeta {
  id: number;
  row_index: number;
  rack_index: number;
  label?: string;
  name?: string;
  x_m?: number;
  y_m?: number;
  width_m?: number;
  depth_m?: number;
  is_blocked?: boolean;
  u_slots?: number;
  heightU?: number;
  rotation?: number;
  tenant?: string;
  tenant_id?: string;
  status?: string;
  role?: string;
  max_power_w?: number;
  max_weight_kg?: number;
}

export interface RoutePoint {
  x: number;
  y: number;
  z: number;
  description: string;
}

export interface CalcResult {
  total_length: number;
  warning: string | null;
  route: RoutePoint[];
}

export interface HistoryEntry {
  id: number;
  srcLabel: string;
  tgtLabel: string;
  cableType: string;
  length: number;
  warning: string | null;
  route: RoutePoint[];
  src: RackMeta;
  tgt: RackMeta;
}

export const CABLE_LIMITS: Record<string, { label: string; maxM: number }> = {
  copper:   { label: 'Медь (RJ45 Cat.6A)',   maxM: 90 },
  dac:      { label: 'DAC (Twinax)',          maxM: 5 },
  mm_fiber: { label: 'Оптика (Многомод)',     maxM: 550 },
  sm_fiber: { label: 'Оптика (Одномод)',      maxM: 10000 },
}

export function useCalculatorLogic(dcCode?: string, currentDc?: DcMeta) {
  const [racks, setRacks] = useState<RackMeta[]>([])
  const [src, setSrc] = useState<RackMeta | null>(null)
  const [srcU, setSrcU] = useState<number>(42)
  const [tgt, setTgt] = useState<RackMeta | null>(null)
  const [tgtU, setTgtU] = useState<number>(42)
  const [cable, setCable] = useState('copper')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<CalcResult | null>(null)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [hid, setHid] = useState(0)

  useEffect(() => {
    if (!dcCode) return
    setSrc(null); setTgt(null); setResult(null); setError(null)
    api.get(`/api/datacenters/${dcCode}/racks`)
      .then(r => {
        const sorted = (r.data as RackMeta[]).sort((a, b) =>
          a.row_index !== b.row_index ? a.row_index - b.row_index : a.rack_index - b.rack_index
        )
        setRacks(sorted)
        const validRacks = sorted.filter(r => r.row_index < 100)
        if (validRacks.length >= 2) { setSrc(validRacks[0]); setTgt(validRacks[validRacks.length - 1]) }
        else if (sorted.length >= 2) { setSrc(sorted[0]); setTgt(sorted[sorted.length - 1]) }
      })
      .catch(e => setError(e.message))
  }, [dcCode])

  const handleSwap = useCallback(() => { 
    setSrc(tgt); setTgt(src); 
    setSrcU(tgtU); setTgtU(srcU);
    setResult(null); 
  }, [src, tgt, srcU, tgtU])

  const handleCalc = useCallback(async () => {
    if (!src || !tgt || !dcCode) return
    setLoading(true); setError(null); setResult(null)
    try {
      const res = await api.post('/api/calculator/calculate', {
        datacenter_code: dcCode, cable_type: cable,
        source_row: src.row_index, source_rack: src.rack_index,
        target_row: tgt.row_index, target_rack: tgt.rack_index,
        source_u_slot: srcU, target_u_slot: tgtU,
      })
      setResult(res.data)
      setHistory(h => [{
        id: hid, src, tgt,
        srcLabel: rackLabel(src, currentDc), tgtLabel: rackLabel(tgt, currentDc),
        cableType: cable, length: res.data.total_length,
        warning: res.data.warning, route: res.data.route,
      }, ...h].slice(0, 10))
      setHid(x => x + 1)
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Ошибка расчёта')
    } finally { setLoading(false) }
  }, [src, tgt, dcCode, cable, hid, currentDc, srcU, tgtU])

  const handleRackClick = useCallback((rack: RackMeta) => {
    if (!src) { setSrc(rack); return }
    if (!tgt) { setTgt(rack); return }
    setSrc(rack); setTgt(null); setResult(null)
  }, [src, tgt])

  return {
    racks, src, setSrc, srcU, setSrcU, tgt, setTgt, tgtU, setTgtU,
    cable, setCable, loading, error, setError, result, setResult,
    history, setHistory, handleSwap, handleCalc, handleRackClick
  }
}
